import { useState, useEffect, useCallback } from "react";
import { ClientOverviewRepositoryImpl } from "../../data/repositories/ClientOverviewRepositoryImpl";
import { GetTwelveMonthExpenseInsightsUseCase } from "../../domain/use-cases/GetTwelveMonthExpenseInsightsUseCase";
import type { TwelveMonthExpenseInsightItem } from "../../domain/entities/TwelveMonthExpenseInsightsEntity";

const repository = new ClientOverviewRepositoryImpl();
const useCase = new GetTwelveMonthExpenseInsightsUseCase(repository);

export interface TwelveMonthExpenseInsightsData {
  insights: TwelveMonthExpenseInsightItem[];
  rangeLabel: string;
  monthCount: number;
  noInsights: boolean;
  message: string | null;
  loading: boolean;
  error: string | null;
}

export interface UseTwelveMonthExpenseInsightsOptions {
  /** When false, the insights API is not called. Set true only after twelve-month trend has loaded. */
  enabled?: boolean;
}

export function useTwelveMonthExpenseInsights(
  options?: UseTwelveMonthExpenseInsightsOptions,
): TwelveMonthExpenseInsightsData {
  const enabled = options?.enabled ?? true;

  const [insights, setInsights] = useState<TwelveMonthExpenseInsightItem[]>([]);
  const [rangeLabel, setRangeLabel] = useState("");
  const [monthCount, setMonthCount] = useState(0);
  const [noInsights, setNoInsights] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    if (!enabled) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const entity = await useCase.execute();
      setInsights(entity.insights);
      setRangeLabel(entity.rangeLabel);
      setMonthCount(entity.monthCount);
      setNoInsights(entity.noInsights);
      setMessage(entity.message);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to load monthly trend insights",
      );
      setInsights([]);
      setRangeLabel("");
      setMonthCount(0);
      setNoInsights(true);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchInsights();
  }, [enabled, fetchInsights]);

  return {
    insights,
    rangeLabel,
    monthCount,
    noInsights,
    message,
    loading,
    error,
  };
}
